import type { SensorUomResponse } from '../../Services/serviceURL';
import {
  AssetDetails,
  AssetType,
  mergeAllUomOptions,
  OriginalAssetFormSnapshot,
  UIParameter,
} from './createPlantDetailsModel';

type AssetTypeParameter = AssetType['parameters'][number];
type SavedAssetParameter = AssetDetails['parameters'][number];

function findSavedAssetParameter(
  asset: AssetDetails,
  typeParam: AssetTypeParameter
): SavedAssetParameter | undefined {
  if (!Array.isArray(asset.parameters)) {
    return undefined;
  }

  return asset.parameters.find((param) => {
    if (typeParam.isUserConfigurable === true) {
      return typeParam.tag === param.tag;
    }
    return typeParam.sensorName === param.sensorName;
  });
}

function getStandardProbeFields(
  savedParam: SavedAssetParameter | undefined,
  hasUom: boolean
): Pick<UIParameter, 'businessFriendlyName' | 'units' | 'selectedSensorType'> {
  if (!savedParam) {
    return { businessFriendlyName: '', units: '' };
  }

  return {
    businessFriendlyName: savedParam.displayName || '',
    units: hasUom ? savedParam.unit || '' : '',
  };
}

function getCustomProbeFields(
  savedParam: SavedAssetParameter | undefined,
  hasUom: boolean,
  validUoms: SensorUomResponse[]
): Pick<UIParameter, 'businessFriendlyName' | 'units' | 'selectedSensorType' | 'unitOptions'> {
  const unitOptions = mergeAllUomOptions(validUoms);
  const hasDisplayName = Boolean(savedParam?.displayName?.trim());

  if (!hasDisplayName) {
    return {
      businessFriendlyName: '',
      units: '',
      selectedSensorType: '',
      unitOptions,
    };
  }

  const sensorUoms = validUoms.find((uom) => uom.sensorName === savedParam?.sensorName);
  const selectedSensorType = sensorUoms ? savedParam?.sensorName : 'Custom';

  return {
    businessFriendlyName: savedParam?.displayName ?? '',
    units: hasUom ? savedParam?.unit || '' : '',
    selectedSensorType,
    unitOptions,
  };
}

function buildUiParameterFromTypeParam(
  asset: AssetDetails,
  typeParam: AssetTypeParameter,
  validUoms: SensorUomResponse[]
): UIParameter {
  const savedParam = findSavedAssetParameter(asset, typeParam);
  const hasUom = typeParam.hasUom;
  const baseUnitOptions = Array.isArray(typeParam.units) ? typeParam.units : [];

  const probeFields =
    typeParam.isUserConfigurable === true && validUoms.length > 0
      ? getCustomProbeFields(savedParam, hasUom, validUoms)
      : {
          ...getStandardProbeFields(savedParam, hasUom),
          unitOptions: baseUnitOptions,
        };

  return {
    tag: typeParam.tag,
    sensor: typeParam.sensorName,
    position: savedParam?.position || typeParam.position || '',
    businessFriendlyName: probeFields.businessFriendlyName,
    value: '',
    units: probeFields.units,
    unitOptions: probeFields.unitOptions ?? baseUnitOptions,
    originalUnits: baseUnitOptions,
    isUserConfigurable: typeParam.isUserConfigurable,
    selectedSensorType: probeFields.selectedSensorType,
    hasUom: typeParam.hasUom,
  };
}

export function buildAssetParametersForSelection(
  asset: AssetDetails,
  assetType: AssetType | undefined,
  validUoms: SensorUomResponse[]
): UIParameter[] {
  if (!assetType?.parameters) {
    return [];
  }

  return assetType.parameters.map((typeParam) =>
    buildUiParameterFromTypeParam(asset, typeParam, validUoms)
  );
}

export function buildOriginalAssetDataFromClick(
  asset: AssetDetails,
  assetParameters: UIParameter[]
): OriginalAssetFormSnapshot {
  return {
    selectedAsset: asset.type,
    assetName: asset.name,
    assetStatus: asset.status,
    gatewayDeviceType: asset.gatewayDeviceType ?? '',
    gatewayDeviceId: asset.gatewayDeviceId ?? '',
    simNumber: asset.simNumber ?? '',
    controllerType: asset.controllerType ?? '',
    controllerDeviceId: asset.controllerDeviceId ?? '',
    parameters: assetParameters.map((param) => ({
      ...param,
      businessFriendlyName: param.businessFriendlyName || '',
      units: param.units || '',
      selectedSensorType: param.selectedSensorType || '',
      value: param.value || '',
      unitOptions: param.unitOptions ? [...param.unitOptions] : [],
    })),
  };
}
